import React, { useEffect, useState } from "react";
import axios from "axios";
import { Users, MessageCircle, Calendar, Coins, ShoppingBag, TrendingUp, Eye, Award } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const PERIODS = [
  { key: "today", label: "Сегодня" },
  { key: "yesterday", label: "Вчера" },
  { key: "7d", label: "7 дней" },
  { key: "30d", label: "30 дней" },
];

const fmt = (n) => Math.floor(Number(n) || 0).toLocaleString("ru-RU");

function Card({ icon: Icon, title, value, sub, accent = "#C5A059", testId }) {
  return (
    <div data-testid={testId} style={{
      background: "white", border: "1px solid #e5e7eb", borderRadius: "10px",
      padding: "18px 20px", display: "flex", gap: "14px", alignItems: "flex-start",
    }}>
      <div style={{ width: 38, height: 38, borderRadius: 8, background: `${accent}18`, color: accent, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={20} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: "Manrope", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase", color: "#6b7280", marginBottom: 4 }}>{title}</div>
        <div style={{ fontFamily: "Cormorant Garamond", fontSize: 26, fontWeight: 600, color: "#0A0A0A", lineHeight: 1 }}>{value}</div>
        {sub && <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#94a3b8", marginTop: 3 }}>{sub}</div>}
      </div>
    </div>
  );
}

function ListBlock({ title, items, valueKey, valueLabel, nameKey = "name", emptyText }) {
  return (
    <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 10, padding: "18px 20px" }}>
      <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: 20, fontWeight: 500, color: "#0A0A0A", margin: "0 0 12px" }}>{title}</h3>
      {(!items || items.length === 0) ? (
        <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#94a3b8" }}>{emptyText || "Нет данных"}</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.slice(0, 10).map((it, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: i === items.length - 1 ? "none" : "1px solid #f3f4f6" }}>
              <span style={{ fontFamily: "Manrope", fontSize: 12, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "75%" }}>
                <span style={{ color: "#94a3b8", marginRight: 6 }}>{i + 1}.</span>
                {it[nameKey] || it.page || "—"}
              </span>
              <span style={{ fontFamily: "Manrope", fontSize: 12, fontWeight: 600, color: "#C5A059", whiteSpace: "nowrap" }}>
                {fmt(it[valueKey])}{valueLabel ? ` ${valueLabel}` : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminAnalytics() {
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/admin/analytics?period=${period}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [period]);

  const topPagesChartData = (data?.top_pages || []).slice(0, 7).map(p => ({
    name: (p.page || "/").replace(/^\/app\//, "").substring(0, 18),
    views: p.views,
  }));

  return (
    <div data-testid="admin-analytics">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 18 }}>
        <h2 style={{ fontFamily: "Cormorant Garamond", fontSize: 30, fontWeight: 500, color: "#0A0A0A", margin: 0 }}>Аналитика</h2>
        <div style={{ display: "flex", gap: 6 }}>
          {PERIODS.map(p => (
            <button key={p.key} data-testid={`period-${p.key}`} onClick={() => setPeriod(p.key)}
              style={{
                background: period === p.key ? "#0A0A0A" : "white",
                color: period === p.key ? "white" : "#374151",
                border: `1px solid ${period === p.key ? "#0A0A0A" : "#e5e7eb"}`,
                borderRadius: 6, padding: "7px 14px", cursor: "pointer",
                fontFamily: "Manrope", fontSize: 12, fontWeight: 600,
              }}>{p.label}</button>
          ))}
        </div>
      </div>

      {loading && <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontFamily: "Manrope" }}>Загрузка…</div>}

      {data && !loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Top row: users / appointments / WhatsApp / sales */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
            <Card testId="m-new-users" icon={Users} title="Новые регистрации" value={fmt(data.users.new)} sub={`Всего: ${fmt(data.users.total)}`} />
            <Card testId="m-dau" icon={TrendingUp} title="DAU (активные)" value={fmt(data.users.dau)} sub="Уникальные пользователи" accent="#16a34a" />
            <Card testId="m-appts" icon={Calendar} title="Записи на примерку" value={fmt(data.appointments.new)} sub={`Подтверждено: ${fmt(data.appointments.confirmed)} · Отменено: ${fmt(data.appointments.cancelled)}`} accent="#3b82f6" />
            <Card testId="m-wa" icon={MessageCircle} title="Переходы в WhatsApp" value={fmt(data.whatsapp.clicks)} sub={`Уникальных: ${fmt(data.whatsapp.unique_users)}`} accent="#25D366" />
          </div>

          {/* Sales + bonuses */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
            <Card testId="m-sales-count" icon={ShoppingBag} title="Покупок" value={fmt(data.sales.count)} sub={`Сумма: ${fmt(data.sales.total)} ₸`} accent="#C5A059" />
            <Card testId="m-sales-avg" icon={ShoppingBag} title="Средний чек" value={`${fmt(data.sales.avg)} ₸`} sub={`Начислено кэшбэка: ${fmt(data.sales.bonus_earned)} ₸`} accent="#C5A059" />
            <Card testId="m-bonus-cred" icon={Coins} title="Начислено бонусов" value={fmt(data.bonuses.credited)} sub={`${fmt(data.bonuses.users_credited)} клиентов получили`} accent="#10b981" />
            <Card testId="m-bonus-spent" icon={Coins} title="Списано бонусов" value={fmt(data.bonuses.spent)} sub={`${fmt(data.bonuses.users_spent)} клиентов использовали`} accent="#ef4444" />
          </div>

          {/* Conversions */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
            <Card testId="m-conv-wa" icon={MessageCircle} title="Регистрация → WhatsApp" value={`${data.conversions.reg_to_wa_pct}%`} accent="#25D366" />
            <Card testId="m-conv-appt" icon={Calendar} title="Регистрация → запись" value={`${data.conversions.reg_to_appointment_pct}%`} accent="#3b82f6" />
            <Card testId="m-system-balance" icon={Award} title="Бонусный баланс системы" value={`${fmt(data.bonuses.system_balance)} ₸`} sub="Сумма всех бонусов клиентов" />
          </div>

          {/* Top pages chart */}
          {topPagesChartData.length > 0 && (
            <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 10, padding: "18px 20px" }}>
              <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: 20, fontWeight: 500, color: "#0A0A0A", margin: "0 0 12px" }}>Просмотры по разделам</h3>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={topPagesChartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                  <YAxis tick={{ fontSize: 11, fontFamily: "Manrope" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontFamily: "Manrope", fontSize: 12 }} />
                  <Bar dataKey="views" fill="#C5A059" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Lists row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 12 }}>
            <ListBlock title="ТОП-10 страниц" items={data.top_pages} valueKey="views" valueLabel="просм." nameKey="page" emptyText="Пока нет просмотров. После Redeploy и захода клиентов появятся данные." />
            <ListBlock title="ТОП-10 товаров" items={data.top_products} valueKey="views" valueLabel="просм." emptyText="Нет просмотров товаров" />
            <ListBlock title="ТОП-10 брендов" items={data.top_brands} valueKey="views" valueLabel="просм." emptyText="Нет просмотров брендов" />
          </div>

          {/* Top clients table */}
          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 10, padding: "18px 20px" }}>
            <h3 style={{ fontFamily: "Cormorant Garamond", fontSize: 20, fontWeight: 500, color: "#0A0A0A", margin: "0 0 12px" }}>ТОП-20 активных клиентов</h3>
            {(!data.top_clients || data.top_clients.length === 0) ? (
              <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#94a3b8" }}>Пока нет данных об активности клиентов</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Manrope", fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: "#f9fafb", textAlign: "left" }}>
                      <th style={{ padding: "10px 12px", color: "#6b7280", fontWeight: 600, letterSpacing: "0.06em", fontSize: 10, textTransform: "uppercase" }}>Клиент</th>
                      <th style={{ padding: "10px 12px", color: "#6b7280", fontWeight: 600, letterSpacing: "0.06em", fontSize: 10, textTransform: "uppercase" }}>Телефон</th>
                      <th style={{ padding: "10px 12px", color: "#6b7280", fontWeight: 600, letterSpacing: "0.06em", fontSize: 10, textTransform: "uppercase", textAlign: "right" }}>Действия</th>
                      <th style={{ padding: "10px 12px", color: "#6b7280", fontWeight: 600, letterSpacing: "0.06em", fontSize: 10, textTransform: "uppercase", textAlign: "center" }}>WA</th>
                      <th style={{ padding: "10px 12px", color: "#6b7280", fontWeight: 600, letterSpacing: "0.06em", fontSize: 10, textTransform: "uppercase", textAlign: "center" }}>Примерка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_clients.map((c, i) => (
                      <tr key={c.id} style={{ borderTop: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "10px 12px", color: "#0A0A0A", fontWeight: 600 }}>{i + 1}. {c.name || "—"}</td>
                        <td style={{ padding: "10px 12px", color: "#475569" }}>{c.phone}</td>
                        <td style={{ padding: "10px 12px", color: "#C5A059", fontWeight: 700, textAlign: "right" }}>{fmt(c.views)}</td>
                        <td style={{ padding: "10px 12px", textAlign: "center", color: c.wa_clicked ? "#25D366" : "#cbd5e1" }}>{c.wa_clicked ? "✓" : "—"}</td>
                        <td style={{ padding: "10px 12px", textAlign: "center", color: c.appointment_made ? "#3b82f6" : "#cbd5e1" }}>{c.appointment_made ? "✓" : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
